import { BASELINE_SCENARIO, chargerCasMetier, type CasMetier } from '@easy2log/simulation-engine';
import catalogueBrut from '../../../config/simulation/scenarios/cas-metier.json';

/**
 * Traduction entre les réglages de l'écran et le scénario du moteur.
 *
 * L'écran ne réglait que 4 des ~35 paramètres : sans surcharge, c'est
 * `BASELINE_SCENARIO` qui s'appliquait — et son propre commentaire le qualifie,
 * « c'est l'hypothèse OPTIMISTE, elle n'est pas réaliste ». Une livraison MFI se
 * déclarait donc conforme sans qu'aucune condition dégradée n'ait été observée,
 * alors que l'invariant nº3 (ne jamais arbitrer un conflit) ne s'exerce QUE dans
 * ces conditions.
 *
 * Les valeurs vivent dans `config/simulation/scenarios/cas-metier.json` (§25) et
 * le FORMAT dans `@easy2log/simulation-engine` : ce fichier ne fait que relier
 * les deux à des libellés français.
 */

export const CAS_METIER: CasMetier[] = chargerCasMetier(catalogueBrut);

/**
 * Forme de l'intervalle de libération, RAPPORTÉE à sa moyenne.
 *
 * Le curseur de cadence ne règle qu'une moyenne ; l'écart-type et le plancher
 * doivent suivre. Ils étaient écrits en dur — `stddev_s: 20, min_s: 45` — et le
 * plancher rendait le curseur INERTE sur toute sa moitié basse : `drawDuration`
 * applique `Math.max(min_s, …)`, donc régler 5 s, 25 s ou 40 s produisait
 * exactement le même run qu'à 45 s. Le §19.14 — débarquement saturé, plusieurs
 * remorques dans la même fenêtre — était inatteignable depuis l'écran, alors que
 * l'aide du curseur le cite en exemple.
 *
 * Les rapports viennent de la référence du moteur, jamais d'un littéral (§25) :
 * 90 s de moyenne, 20 s d'écart, 45 s de plancher. Le préréglage « débarquement
 * saturé » du catalogue tient le même rapport (8 / 2 / 4).
 */
const FORME_CADENCE = {
  ecart: (BASELINE_SCENARIO.trailerReleaseInterval.stddev_s ?? 0) /
    BASELINE_SCENARIO.trailerReleaseInterval.mean_s,
  plancher: (BASELINE_SCENARIO.trailerReleaseInterval.min_s ?? 0) /
    BASELINE_SCENARIO.trailerReleaseInterval.mean_s,
};

/**
 * Deux paramètres d'INTERFACE encore écrits ici.
 *
 * Ils n'ont pas d'équivalent dans `BASELINE_SCENARIO` : ce sont des choix de
 * l'écran, pas du moteur. Les nommer les rend au moins visibles, et le libellé
 * du curseur cite la même valeur — les voir ensemble empêche qu'ils divergent.
 * Leur place définitive est un fichier de `config/` (§25).
 */
const PANNE_TRACTEUR_A_S = 150;
const REPRISE_GNSS_PAR_DEFAUT_S = 5;
/**
 * Le terrain, par défaut. Le moteur garde sa référence optimiste — précision
 * fixe, parc vide, places prises dans l'ordre — parce que ses tests et le banc
 * de tolérance la figent. L'écran, lui, doit montrer un débarquement crédible :
 * un parc en partie occupé par les escales précédentes, un chauffeur qui
 * prend la place libre qu'il voit, une pose à un mètre près, un récepteur dont
 * la précision bouge et dont l'erreur traîne dans une direction.
 */
const OCCUPATION_PARC_PAR_DEFAUT = 0.2;
/**
 * Quand l'occupation est tirée : de presque vide à un tiers plein au plus,
 * selon les escales qui précèdent. Le plafond de 35 % est celui donné par le
 * port le 16 octobre 2026 ; le plancher est une hypothèse.
 */
export const OCCUPATION_PARC_TIREE = { min: 0.05, max: 0.35 };
const VARIABILITE_GNSS_PAR_DEFAUT = 0.5;
const ECART_DE_POSE_M = { lateral: 0.5, longitudinal: 1.5 };

/** Un seul boîtier dérive : c'est ce qui rend l'écart VISIBLE à l'écran. */
const BOITIER_QUI_DERIVE = 'TRACTOR-01';

export interface Reglages {
  // Lecture du QR à bord
  qrLu: number;
  qrMalLu: number;
  qrDouble: number;
  qrTardif: number;
  qrAnticipe: number;

  // Saisie de l'opérateur
  opValide: number;
  opConflit: number;
  opPonctuation: number;
  opDoublon: number;
  opPoste: boolean;
  opRang: boolean;

  // Horloge de la compagnie
  opDeriveS: number;
  opGigueS: number;

  // Signal GNSS
  gnssRepriseS: number;
  gnssEchec: number;
  gnssDegrade: number;
  gnssPrecisionDegradeeM: number;
  gnssVariabilite: number;

  // Réseau 5G du port
  reseauCoupureDebutS: number;
  reseauCoupureDureeS: number;
  reseauDoublon: number;
  reseauDesordre: number;

  // Cadence du débarquement
  cadenceS: number;

  // Aléas du terrain
  horsReferentiel: number;
  /** Part du parc déjà occupée au départ, places tirées au hasard. */
  occupationParc: number;
  /** Le taux lui-même est tiré à chaque run, dans `OCCUPATION_PARC_TIREE`. */
  occupationAuHasard: boolean;
  /** Le chauffeur prend une place libre au hasard, plutôt que la suivante. */
  placesAuHasard: boolean;
  tracteurEnPanne: boolean;

  // Horloge des boîtiers
  deriveBoitierS: number;
}

export const REGLAGES_PAR_DEFAUT: Reglages = {
  qrLu: BASELINE_SCENARIO.qr.read_probability,
  qrMalLu: BASELINE_SCENARIO.qr.misread_probability,
  qrDouble: BASELINE_SCENARIO.qr.duplicate_read_probability,
  qrTardif: BASELINE_SCENARIO.qr.late_read_probability,
  qrAnticipe: BASELINE_SCENARIO.qr.advance_read_probability,

  opValide: BASELINE_SCENARIO.externalApp.identification_probability,
  opConflit: BASELINE_SCENARIO.externalApp.conflict_probability,
  opPonctuation: BASELINE_SCENARIO.externalApp.separator_noise_probability,
  opDoublon: BASELINE_SCENARIO.externalApp.duplicate_probability,
  opPoste: BASELINE_SCENARIO.externalApp.emit_control_post,
  opRang: BASELINE_SCENARIO.externalApp.emit_exit_rank,

  opDeriveS: BASELINE_SCENARIO.externalApp.clock_skew_ms / 1000,
  opGigueS: BASELINE_SCENARIO.externalApp.timestamp_offset.mean_s,

  gnssRepriseS: REPRISE_GNSS_PAR_DEFAUT_S,
  gnssEchec: BASELINE_SCENARIO.gnss.failure_probability,
  gnssDegrade: BASELINE_SCENARIO.gnss.degraded_probability,
  gnssPrecisionDegradeeM: BASELINE_SCENARIO.gnss.accuracy_m.degraded,
  gnssVariabilite: VARIABILITE_GNSS_PAR_DEFAUT,

  reseauCoupureDebutS: 0,
  reseauCoupureDureeS: 0,
  reseauDoublon: BASELINE_SCENARIO.network.duplicate_probability,
  reseauDesordre: BASELINE_SCENARIO.network.reorder_probability,

  cadenceS: BASELINE_SCENARIO.trailerReleaseInterval.mean_s,

  horsReferentiel: BASELINE_SCENARIO.unknownTrailerRatio,
  occupationParc: OCCUPATION_PARC_PAR_DEFAUT,
  occupationAuHasard: true,
  placesAuHasard: true,
  tracteurEnPanne: false,
  deriveBoitierS: 0,
};

/**
 * Construit les surcharges de scénario à partir des réglages.
 *
 * Le résultat est en camelCase et n'énumère que ce que l'écran règle : le
 * Durable Object fusionne PROFONDÉMENT tous les blocs du scénario de référence,
 * donc envoyer `{ qr: { misread_probability: 0.2 } }` ne détruit plus le reste
 * du bloc `qr` — ni `{ controlDuration: { mean_s: 20 } }` ses bornes.
 */
export function construireScenario(
  reglages: Reglages,
  tractorCount: number,
  trailerCount: number,
): Record<string, unknown> {
  const coupure =
    reglages.reseauCoupureDureeS > 0
      ? [
          {
            starts_after_s: reglages.reseauCoupureDebutS,
            duration_s: reglages.reseauCoupureDureeS,
            tractor_refs: [],
          },
        ]
      : [];

  const derives: Record<string, number> = {};
  if (reglages.deriveBoitierS !== 0) {
    // Les faire tous dériver du même montant reviendrait à décaler l'horloge du
    // port, ce qui ne se verrait nulle part.
    derives[BOITIER_QUI_DERIVE] = Math.round(reglages.deriveBoitierS * 1000);
  }

  return {
    tractorCount,
    trailerCount,
    // L'écart et le plancher SUIVENT la moyenne. Un plancher fixe à 45 s
    // annulait toute la moitié basse du curseur : voir `FORME_CADENCE`.
    trailerReleaseInterval: {
      mean_s: reglages.cadenceS,
      stddev_s: arrondiDecimal(reglages.cadenceS * FORME_CADENCE.ecart),
      min_s: arrondiDecimal(reglages.cadenceS * FORME_CADENCE.plancher),
    },
    unknownTrailerRatio: reglages.horsReferentiel,
    clockSkewMs: derives,
    outOfService: reglages.tracteurEnPanne
      ? [
          {
            tractorRef: `TRACTOR-${String(tractorCount).padStart(2, '0')}`,
            atS: PANNE_TRACTEUR_A_S,
          },
        ]
      : [],
    slotAssignment: {
      strategy: reglages.placesAuHasard ? 'random' : 'sequential',
      // Les places du parc au départ sont tirées par le MOTEUR, sur la graine du
      // run : l'écran ne les choisit plus. Il prenait les N premières — un parc
      // rempli de O01 à ON, ce que le terrain ne produit jamais.
      preOccupiedSlotCodes: [],
      preOccupiedRatio: reglages.occupationAuHasard
        ? OCCUPATION_PARC_TIREE
        : reglages.occupationParc,
      depositJitterM: reglages.placesAuHasard ? ECART_DE_POSE_M : { lateral: 0, longitudinal: 0 },
    },
    qr: {
      read_probability: reglages.qrLu,
      misread_probability: reglages.qrMalLu,
      duplicate_read_probability: reglages.qrDouble,
      late_read_probability: reglages.qrTardif,
      advance_read_probability: reglages.qrAnticipe,
    },
    externalApp: {
      identification_probability: reglages.opValide,
      conflict_probability: reglages.opConflit,
      separator_noise_probability: reglages.opPonctuation,
      duplicate_probability: reglages.opDoublon,
      emit_control_post: reglages.opPoste,
      emit_exit_rank: reglages.opRang,
      clock_skew_ms: Math.round(reglages.opDeriveS * 1000),
      timestamp_offset: { mean_s: reglages.opGigueS, stddev_s: 1 },
    },
    gnss: {
      delays: [{ delay_s: reglages.gnssRepriseS, weight: 1 }],
      failure_probability: reglages.gnssEchec,
      degraded_probability: reglages.gnssDegrade,
      accuracy_m: {
        good: BASELINE_SCENARIO.gnss.accuracy_m.good,
        degraded: reglages.gnssPrecisionDegradeeM,
      },
      accuracy_variability: reglages.gnssVariabilite,
    },
    network: {
      outages: coupure,
      duplicate_probability: reglages.reseauDoublon,
      reorder_probability: reglages.reseauDesordre,
    },
  };
}

/** Une durée au dixième de seconde : au-delà, le pas du moteur ne la distingue pas. */
function arrondiDecimal(valeur: number): number {
  return Math.round(valeur * 10) / 10;
}

function nombre(valeur: unknown, defaut: number): number {
  return typeof valeur === 'number' ? valeur : defaut;
}

function booleen(valeur: unknown, defaut: boolean): boolean {
  return typeof valeur === 'boolean' ? valeur : defaut;
}

/**
 * Reflète un cas métier dans les curseurs.
 *
 * L'écran doit MONTRER ce qu'il va envoyer. Appliquer un préréglage sans
 * déplacer les curseurs laisserait croire que le run part sur les valeurs
 * affichées — le genre de mensonge d'écran que ce chantier corrige ailleurs.
 */
export function reglagesDepuisCas(cas: CasMetier): Reglages {
  const s = cas.surcharges as Record<string, unknown>;
  const sousObjet = (cle: string): Record<string, unknown> => {
    const valeur = s[cle];
    return typeof valeur === 'object' && valeur !== null ? (valeur as Record<string, unknown>) : {};
  };

  const qr = sousObjet('qr');
  const ext = sousObjet('externalApp');
  const gnss = sousObjet('gnss');
  const reseau = sousObjet('network');
  const places = sousObjet('slotAssignment');

  const delais = gnss['delays'];
  const premierDelai =
    Array.isArray(delais) && delais.length > 0
      ? nombre((delais[0] as Record<string, unknown>)['delay_s'], REGLAGES_PAR_DEFAUT.gnssRepriseS)
      : REGLAGES_PAR_DEFAUT.gnssRepriseS;

  const coupures = reseau['outages'];
  const premiereCoupure =
    Array.isArray(coupures) && coupures.length > 0
      ? (coupures[0] as Record<string, unknown>)
      : null;

  const precision = sousObjet('gnss')['accuracy_m'] as Record<string, unknown> | undefined;
  const cadence = s['trailerReleaseInterval'] as Record<string, unknown> | undefined;
  const gigue = ext['timestamp_offset'] as Record<string, unknown> | undefined;
  const horsService = s['outOfService'];

  return {
    qrLu: nombre(qr['read_probability'], REGLAGES_PAR_DEFAUT.qrLu),
    qrMalLu: nombre(qr['misread_probability'], REGLAGES_PAR_DEFAUT.qrMalLu),
    qrDouble: nombre(qr['duplicate_read_probability'], REGLAGES_PAR_DEFAUT.qrDouble),
    qrTardif: nombre(qr['late_read_probability'], REGLAGES_PAR_DEFAUT.qrTardif),
    qrAnticipe: nombre(qr['advance_read_probability'], REGLAGES_PAR_DEFAUT.qrAnticipe),

    opValide: nombre(ext['identification_probability'], REGLAGES_PAR_DEFAUT.opValide),
    opConflit: nombre(ext['conflict_probability'], REGLAGES_PAR_DEFAUT.opConflit),
    opPonctuation: nombre(ext['separator_noise_probability'], REGLAGES_PAR_DEFAUT.opPonctuation),
    opDoublon: nombre(ext['duplicate_probability'], REGLAGES_PAR_DEFAUT.opDoublon),
    opPoste: booleen(ext['emit_control_post'], REGLAGES_PAR_DEFAUT.opPoste),
    opRang: booleen(ext['emit_exit_rank'], REGLAGES_PAR_DEFAUT.opRang),

    opDeriveS: nombre(ext['clock_skew_ms'], REGLAGES_PAR_DEFAUT.opDeriveS * 1000) / 1000,
    opGigueS: nombre(gigue?.['mean_s'], REGLAGES_PAR_DEFAUT.opGigueS),

    gnssRepriseS: premierDelai,
    gnssEchec: nombre(gnss['failure_probability'], REGLAGES_PAR_DEFAUT.gnssEchec),
    gnssDegrade: nombre(gnss['degraded_probability'], REGLAGES_PAR_DEFAUT.gnssDegrade),
    gnssPrecisionDegradeeM: nombre(
      precision?.['degraded'],
      REGLAGES_PAR_DEFAUT.gnssPrecisionDegradeeM,
    ),
    gnssVariabilite: nombre(gnss['accuracy_variability'], REGLAGES_PAR_DEFAUT.gnssVariabilite),

    reseauCoupureDebutS: nombre(premiereCoupure?.['starts_after_s'], 0),
    reseauCoupureDureeS: nombre(premiereCoupure?.['duration_s'], 0),
    reseauDoublon: nombre(reseau['duplicate_probability'], REGLAGES_PAR_DEFAUT.reseauDoublon),
    reseauDesordre: nombre(reseau['reorder_probability'], REGLAGES_PAR_DEFAUT.reseauDesordre),

    cadenceS: nombre(cadence?.['mean_s'], REGLAGES_PAR_DEFAUT.cadenceS),

    horsReferentiel: nombre(s['unknownTrailerRatio'], REGLAGES_PAR_DEFAUT.horsReferentiel),
    occupationParc: nombre(places['preOccupiedRatio'], REGLAGES_PAR_DEFAUT.occupationParc),
    occupationAuHasard:
      places['preOccupiedRatio'] === undefined
        ? REGLAGES_PAR_DEFAUT.occupationAuHasard
        : typeof places['preOccupiedRatio'] === 'object',
    placesAuHasard:
      typeof places['strategy'] === 'string'
        ? places['strategy'] === 'random'
        : REGLAGES_PAR_DEFAUT.placesAuHasard,
    tracteurEnPanne: Array.isArray(horsService) && horsService.length > 0,
    deriveBoitierS: 0,
  };
}

/**
 * Scénario d'un cas métier, posé sur le terrain de l'écran.
 *
 * Le cas partait SEUL : le serveur complétait avec la référence du moteur, donc
 * places prises dans l'ordre sur un parc vide et GNSS à précision constante —
 * pendant que l'écran affichait « au hasard » coché. Seuls le parc et la
 * variabilité GNSS viennent du terrain ; tout ce que le cas fixe l'emporte,
 * bloc par bloc.
 */
export function avecTerrain(
  surcharges: Record<string, unknown>,
  terrain: Record<string, unknown>,
  effectifs: { tractorCount: number; trailerCount: number },
): Record<string, unknown> {
  const bloc = (valeur: unknown): Record<string, unknown> =>
    typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur)
      ? (valeur as Record<string, unknown>)
      : {};
  const gnssTerrain = bloc(terrain['gnss']);
  return {
    ...surcharges,
    slotAssignment: { ...bloc(terrain['slotAssignment']), ...bloc(surcharges['slotAssignment']) },
    gnss: {
      ...(gnssTerrain['accuracy_variability'] === undefined
        ? {}
        : { accuracy_variability: gnssTerrain['accuracy_variability'] }),
      ...bloc(surcharges['gnss']),
    },
    ...effectifs,
  };
}

/** Nombre de tracteurs et de remorques imposés par un cas métier, s'il en impose. */
export function effectifsDuCas(cas: CasMetier): { tracteurs?: number; remorques?: number } {
  const s = cas.surcharges as Record<string, unknown>;
  return {
    ...(typeof s['tractorCount'] === 'number' ? { tracteurs: s['tractorCount'] } : {}),
    ...(typeof s['trailerCount'] === 'number' ? { remorques: s['trailerCount'] } : {}),
  };
}
